export type MessageHandler = (message: WebSocketMessage) => void;

export interface WebSocketMessage {
  type: string;
  [key: string]: unknown;
}

class SupportWebSocket {
  private ws: WebSocket | null = null;
  private token: string | null = null;
  private ticketId: string | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private pingInterval: ReturnType<typeof setInterval> | null = null;

  private getWsUrl(): string {
    const wsUrl = process.env.REACT_APP_WS_URL;
    if (wsUrl) {
      return wsUrl;
    }
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const protocol = apiUrl.startsWith('https') ? 'wss:' : 'ws:';
    const host = apiUrl.replace(/^https?:\/\//, '');
    return `${protocol}//${host}/ws/support`;
  }

  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const response = await fetch(`${apiUrl}/api/public/auth/ws-token`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to get WebSocket token');
      }

      const data = await response.json();
      if (!data.success || !data.token) {
        throw new Error('Invalid token response');
      }

      this.token = data.token;
    } catch (err) {
      this.isConnecting = false;
      throw err;
    }

    return new Promise((resolve, reject) => {
      const wsUrl = this.getWsUrl();
      console.log('[Player WS] Connecting to:', wsUrl);

      this.ws = new WebSocket(wsUrl);

      // Wait for auth_success before resolving
      let authTimeout: ReturnType<typeof setTimeout>;

      const authHandler = (event: MessageEvent) => {
        try {
          const message = JSON.parse(event.data);
          if (message.type === 'auth_success') {
            clearTimeout(authTimeout);
            console.log('[Player WS] Authenticated successfully');
            resolve();
          } else if (message.type === 'error' && message.error) {
            clearTimeout(authTimeout);
            reject(new Error(message.error));
          }
        } catch {
          // Ignore parse errors, will be handled by main handler
        }
      };

      this.ws.onopen = () => {
        console.log('[Player WS] Connected, authenticating...');
        this.isConnecting = false;
        this.reconnectAttempts = 0;

        // Add temporary auth handler
        this.ws!.addEventListener('message', authHandler);

        // Send auth and start ping
        this.send({ type: 'auth', token: this.token });
        this.startPing();

        // Timeout for auth
        authTimeout = setTimeout(() => {
          this.ws?.removeEventListener('message', authHandler);
          console.warn('[Player WS] Auth timeout, resolving anyway');
          resolve(); // Resolve anyway after timeout
        }, 5000);
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as WebSocketMessage;
          this.handleMessage(message);
        } catch (err) {
          console.error('[Player WS] Failed to parse message:', err);
        }
      };

      this.ws.onclose = (event) => {
        console.log('[Player WS] Disconnected:', event.code, event.reason);
        this.isConnecting = false;
        this.stopPing();
        clearTimeout(authTimeout);
        this.notifyHandlers({ type: 'disconnected' });

        if (event.code !== 1000 && this.token) {
          this.attemptReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('[Player WS] Error:', error);
        this.isConnecting = false;
        clearTimeout(authTimeout);
        reject(error);
      };
    });
  }

  disconnect() {
    this.token = null;
    this.ticketId = null;
    this.stopPing();

    if (this.ws) {
      this.ws.close(1000, 'User disconnected');
      this.ws = null;
    }
  }

  joinTicket(ticketId: string) {
    this.ticketId = ticketId;
    // Send client info when joining ticket so admin can see it
    this.getClientInfoWithIP().then(clientInfo => {
      this.send({ type: 'join_ticket', ticketId, clientInfo });
    });
  }

  leaveTicket() {
    if (this.ticketId) {
      this.send({ type: 'leave_ticket', ticketId: this.ticketId });
      this.ticketId = null;
    }
  }

  sendMessage(content: string, messageId?: string) {
    this.send({
      type: 'message',
      content,
      messageId: messageId || Date.now().toString(),
    });
  }

  sendTyping() {
    this.send({ type: 'typing' });
  }

  stopTyping() {
    this.send({ type: 'stop_typing' });
  }

  // Player closes/ends the ticket
  closeTicket(ticketId: string) {
    this.send({ type: 'player_close_ticket', ticketId });
    this.ticketId = null;
  }

  // Notify server that a new ticket was created (so admins can be notified)
  notifyTicketCreated(ticket: { id: string; subject: string; subjectLabel: string }) {
    // Collect client info with IP
    this.getClientInfoWithIP().then(clientInfo => {
      this.send({ type: 'ticket_created', ticket, clientInfo });
    });
  }

  // Send client info update (browser, page, etc.)
  sendClientInfo() {
    this.getClientInfoWithIP().then(clientInfo => {
      this.send({ type: 'client_info', clientInfo });
    });
  }

  // Cache for client IP
  private cachedIP: string | null = null;

  // Fetch client's public IP address
  private async fetchClientIP(): Promise<string> {
    if (this.cachedIP) return this.cachedIP;

    try {
      // Try ipify first (most reliable, free)
      const response = await fetch('https://api.ipify.org?format=json', {
        signal: AbortSignal.timeout(3000)
      });
      if (response.ok) {
        const data = await response.json();
        this.cachedIP = data.ip;
        return data.ip;
      }
    } catch {
      // Silently fail, try next
    }

    try {
      // Fallback to ipapi
      const response = await fetch('https://ipapi.co/ip/', {
        signal: AbortSignal.timeout(3000)
      });
      if (response.ok) {
        const ip = await response.text();
        this.cachedIP = ip.trim();
        return this.cachedIP;
      }
    } catch {
      // Silently fail
    }

    return '';
  }

  // Collect client browser/page info
  private getClientInfo() {
    if (typeof window === 'undefined') return {};

    const ua = navigator.userAgent;
    let browser = 'Unknown';
    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';

    return {
      browser,
      userAgent: ua,
      currentPath: window.location.pathname,
      currentUrl: window.location.href,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ip: this.cachedIP || undefined,
    };
  }

  // Async version that fetches IP first
  private async getClientInfoWithIP() {
    const ip = await this.fetchClientIP();
    const info = this.getClientInfo();
    return { ...info, ip };
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getCurrentTicketId(): string | null {
    return this.ticketId;
  }

  private send(message: object) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('[Player WS] Cannot send message, not connected');
    }
  }

  private handleMessage(message: WebSocketMessage) {
    console.log('[Player WS] Received:', message.type);
    this.notifyHandlers(message);
  }

  private notifyHandlers(message: WebSocketMessage) {
    this.messageHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (err) {
        console.error('[Player WS] Handler error:', err);
      }
    });
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[Player WS] Max reconnect attempts reached');
      this.notifyHandlers({ type: 'reconnect_failed' });
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`[Player WS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      this.connect().then(() => {
        if (this.ticketId) {
          this.joinTicket(this.ticketId);
        }
      }).catch(err => {
        console.error('[Player WS] Reconnect failed:', err);
      });
    }, delay);
  }

  private startPing() {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      this.send({ type: 'ping' });
    }, 30000);
  }

  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}

export const supportWebSocket = new SupportWebSocket();
